import { useState, useEffect, useRef } from 'react'
import { usePreferences } from '../contexts/PreferencesContext'
import { 
    Minimize2,
    Settings,
    Volume2,
    VolumeX,
    Play,
    Pause,
    SkipForward,
    RotateCw,
    Grid,
    Clock,
    Eye,
    EyeOff,
    ChevronUp,
    ChevronDown,
    X
} from 'lucide-react'
import ScreenThumbnailSelector from './ScreenThumbnailSelector'

const FloatingControls = ({
    isVisible = true,
    isFullscreen = false,
    onExitFullscreen,
    onScreenChange,
    onEditScreen,
    onOpenScreenManager,
    availableCameras = [],
    autoHideDelay = 3000,
    currentScreenCameras = [],
    rotationState = null // Add rotation state from AutoRotationManager
}) => {
    const {
        preferences,
        updateAutoRotation,
        toggleGlobalMute,
        setCameraVolume
    } = usePreferences()

    const [isHovered, setIsHovered] = useState(false)
    const [showVolumePanel, setShowVolumePanel] = useState(false)
    const [showSettings, setShowSettings] = useState(false)
    const [isAutoHiding, setIsAutoHiding] = useState(false)
    // Get rotation time from AutoRotationManager instead of local state
    const rotationTimeLeft = rotationState?.timeRemaining || 0
    const [isMinimized, setIsMinimized] = useState(false)

    const hideTimeoutRef = useRef(null)
    const rotationIntervalRef = useRef(null)
    const controlsRef = useRef(null)

    const autoRotation = preferences.screens.autoRotation
    const volumeSettings = preferences.volumeSettings
    const activeScreen = preferences.screens.activeScreenId

    // Auto-hide logic
    useEffect(() => {
        if (!isFullscreen || isHovered || showVolumePanel || showSettings) {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current)
                hideTimeoutRef.current = null
            }
            setIsAutoHiding(false)
            return
        }

        hideTimeoutRef.current = setTimeout(() => {
            setIsAutoHiding(true)
        }, autoHideDelay)

        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current)
            }
        }
    }, [isFullscreen, isHovered, showVolumePanel, showSettings, autoHideDelay])

    // Remove this conflicting timer - AutoRotationManager handles the actual rotation
    // This was causing the issue where countdown showed but screens never advanced

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (!isFullscreen) return

            switch (event.key) {
                case 'Escape':
                    onExitFullscreen?.()
                    break
                case ' ':
                    event.preventDefault()
                    updateAutoRotation({ enabled: !autoRotation.enabled })
                    break
                case 'm':
                case 'M':
                    event.preventDefault()
                    toggleGlobalMute()
                    break
                case 's':
                case 'S':
                    event.preventDefault()
                    setShowSettings(!showSettings)
                    break
                case 'h':
                case 'H':
                    event.preventDefault()
                    setIsMinimized(!isMinimized)
                    break
                default:
                    // Number keys for screen switching
                    if (event.key >= '1' && event.key <= '9') {
                        event.preventDefault()
                        const screenIndex = parseInt(event.key) - 1
                        const screens = Object.values(preferences.screens?.screenConfigs || {})
                        if (screens[screenIndex]) {
                            onScreenChange?.(screens[screenIndex].id)
                        }
                    }
                    break
            }
        }

        document.addEventListener('keydown', handleKeyPress)
        return () => document.removeEventListener('keydown', handleKeyPress)
    }, [isFullscreen, autoRotation.enabled, showSettings, isMinimized, preferences.screens?.screenConfigs, onExitFullscreen, onScreenChange, updateAutoRotation, toggleGlobalMute])

    const handleVolumeChange = (cameraId, volume) => {
        setCameraVolume(cameraId, volume / 100)
    }

    const toggleRotation = () => {
        updateAutoRotation({ enabled: !autoRotation.enabled })
    }

    const updateRotationInterval = (interval) => {
        updateAutoRotation({ interval: parseInt(interval) })
    }

    if (!isVisible || (!isFullscreen && !isVisible)) return null

    const shouldShow = !isAutoHiding || isHovered || showVolumePanel || showSettings || isMinimized

    return (
        <div
            ref={controlsRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`fixed top-4 right-4 z-50 transition-all duration-300 ${
                shouldShow ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
            } ${isMinimized ? 'scale-75' : 'scale-100'}`}
        >
            <div className="bg-black/95 backdrop-blur-sm text-gray-100 rounded-lg shadow-xl border border-gray-600">
                {/* Minimized State */}
                {isMinimized ? (
                    <div className="p-3">
                        <div className="flex items-center gap-3">
                            {/* Auto-rotation indicator */}
                            {autoRotation.enabled && (
                                <div className="flex items-center gap-1 text-green-400">
                                    <RotateCw className="w-4 h-4 animate-spin" />
                                    <span className="text-sm font-mono">{rotationTimeLeft}s</span>
                                </div>
                            )}
                            
                            {/* Volume indicator */}
                            <div className="flex items-center gap-1">
                                {volumeSettings.globalMuted ? (
                                    <VolumeX className="w-4 h-4 text-red-400" />
                                ) : (
                                    <Volume2 className="w-4 h-4 text-blue-400" />
                                )}
                            </div>

                            {/* Expand button */}
                            <button
                                onClick={() => setIsMinimized(false)}
                                className="p-1 hover:bg-gray-600/50 rounded text-gray-200"
                            >
                                <ChevronUp className="w-4 h-4" />
                            </button>

                            {/* Exit button */}
                            <button
                                onClick={onExitFullscreen}
                                className="p-1 hover:bg-red-600/50 rounded text-red-400"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold text-gray-100">Fullscreen Controls</div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setIsMinimized(true)}
                                    className="p-1 hover:bg-gray-600/50 rounded text-gray-200"
                                    title="Minimize (H)"
                                >
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={onExitFullscreen}
                                    className="p-1 hover:bg-red-600/50 rounded text-red-400"
                                    title="Exit Fullscreen (Esc)"
                                >
                                    <Minimize2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Screen Selector */}
                        <div>
                            <ScreenThumbnailSelector
                                onScreenChange={onScreenChange}
                                onEditScreen={onEditScreen}
                                onOpenScreenManager={onOpenScreenManager}
                                availableCameras={availableCameras}
                                isFullscreen={true}
                                showAutoRotationControls={true}
                            />
                        </div>

                        {/* Auto-Rotation Controls */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-200">Auto-Rotation</span>
                                <div className="flex items-center gap-2">
                                    {autoRotation.enabled && rotationTimeLeft > 0 && (
                                        <div className="flex items-center gap-1 text-green-400">
                                            <Clock className="w-3 h-3" />
                                            <span className="text-xs font-mono">{rotationTimeLeft}s</span>
                                        </div>
                                    )}
                                    <button
                                        onClick={toggleRotation}
                                        className={`p-2 rounded transition-colors ${
                                            autoRotation.enabled 
                                                ? 'bg-green-600 hover:bg-green-700 text-white' 
                                                : 'bg-gray-600 hover:bg-gray-700 text-gray-200'
                                        }`}
                                        title={`${autoRotation.enabled ? 'Pause' : 'Play'} rotation (Space)`}
                                    >
                                        {autoRotation.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {autoRotation.enabled && (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-300">Interval:</span>
                                    <select
                                        value={autoRotation.interval}
                                        onChange={(e) => updateRotationInterval(e.target.value)}
                                        className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm text-gray-200 focus:border-green-500 focus:outline-none"
                                    >
                                        <option value="10">10s</option>
                                        <option value="15">15s</option>
                                        <option value="30">30s</option>
                                        <option value="60">1m</option>
                                        <option value="120">2m</option>
                                        <option value="300">5m</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Volume Controls */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-200">Audio</span>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setShowVolumePanel(!showVolumePanel)}
                                        className="p-2 bg-gray-600 hover:bg-gray-700 rounded text-gray-200"
                                        title="Volume settings"
                                    >
                                        <Volume2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={toggleGlobalMute}
                                        className={`p-2 rounded transition-colors ${
                                            volumeSettings.globalMuted 
                                                ? 'bg-red-600 hover:bg-red-700 text-white' 
                                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                        }`}
                                        title={`${volumeSettings.globalMuted ? 'Unmute' : 'Mute'} all (M)`}
                                    >
                                        {volumeSettings.globalMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {/* Volume Panel */}
                            {showVolumePanel && currentScreenCameras.length > 0 && (
                                <div className="bg-gray-800/80 rounded-lg p-3 space-y-2 border border-gray-600">
                                    <div className="text-xs text-gray-200 mb-2 font-medium">Individual Camera Volumes</div>
                                    {currentScreenCameras.map(camera => {
                                        const volume = (volumeSettings.individualVolumes[camera._id] || 0.5) * 100
                                        return (
                                            <div key={camera._id} className="flex items-center gap-2">
                                                <span className="text-xs text-gray-200 flex-1 truncate">
                                                    {camera.name}
                                                </span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={volume}
                                                    onChange={(e) => handleVolumeChange(camera._id, e.target.value)}
                                                    className="w-20 accent-green-500"
                                                />
                                                <span className="text-xs text-gray-200 w-8 font-mono">
                                                    {Math.round(volume)}%
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Quick Actions */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-600">
                            <div className="text-xs text-gray-300">
                                Press H to minimize, Esc to exit
                            </div>
                            <button
                                onClick={() => setShowSettings(!showSettings)}
                                className="p-1 hover:bg-gray-600/50 rounded text-gray-200"
                                title="Settings (S)"
                            >
                                <Settings className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Settings Panel */}
                        {showSettings && (
                            <div className="bg-gray-800/80 rounded-lg p-3 space-y-3 border border-gray-600">
                                <div className="text-xs text-gray-200 mb-2 font-medium">Display Settings</div>
                                
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={preferences.screens.autoRotation.pauseOnActivity}
                                        onChange={(e) => updateAutoRotation({ pauseOnActivity: e.target.checked })}
                                        className="rounded accent-green-500"
                                    />
                                    <span className="text-xs text-gray-200">Pause on motion</span>
                                </label>

                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={preferences.screens.autoRotation.adaptiveTiming}
                                        onChange={(e) => updateAutoRotation({ adaptiveTiming: e.target.checked })}
                                        className="rounded accent-green-500"
                                    />
                                    <span className="text-xs text-gray-200">Adaptive timing</span>
                                </label>

                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-200">Auto-hide delay:</span>
                                    <select
                                        value={autoHideDelay / 1000}
                                        onChange={(e) => {
                                            // This would need to be passed up to parent component
                                            console.log('Auto-hide delay changed:', e.target.value)
                                        }}
                                        className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-gray-200 focus:border-green-500 focus:outline-none"
                                    >
                                        <option value="1">1s</option>
                                        <option value="3">3s</option>
                                        <option value="5">5s</option>
                                        <option value="10">10s</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Progress bar for rotation countdown */}
            {autoRotation.enabled && !isMinimized && rotationTimeLeft > 0 && (
                <div className="mt-2 bg-black/60 rounded-full h-1 overflow-hidden">
                    <div 
                        className="h-full bg-green-400 transition-all duration-1000 ease-linear"
                        style={{ 
                            width: `${((autoRotation.interval - rotationTimeLeft) / autoRotation.interval) * 100}%` 
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default FloatingControls